import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { RequestOptions, Result } from './types'
import { formatDataDate, joinTimeStamp, transformResponseTime } from './helper'
import { HttpMethod } from '@/enums/httpEnum'
import { isString } from '@/utils/is'
import { checkException } from './checkException'
import { useMessage } from '@/composables/useMessage'
import { storageProxy, uuid } from '@/utils'
// import { useLocale } from '@/composables/useLocale'
// import { CacheKeys } from '@/enums/cacheEnum'
// import { cookies } from '@/utils'
import { useUserStoreWithOut } from '@/store-pinia/modules/user'
import { getLocaleInfoCache } from '@/locales/helper'

export interface CreateAxiosOptions extends AxiosRequestConfig {
  authenticationScheme?: string
  urlPrefix?: string
  transform?: AxiosTransform
  requestOptions?: RequestOptions
}

export abstract class AxiosTransform {
  requestBeforeHook?: (config: AxiosRequestConfig, opts: RequestOptions) => AxiosRequestConfig
  transformResponseHook?: (response: AxiosResponse<Result>, opts: RequestOptions) => any
  requestCatchHook?: (err: AxiosError | Error, opts: RequestOptions) => Promise<any>
  requestInterceptors?: (config: AxiosRequestConfig, opts: CreateAxiosOptions) => AxiosRequestConfig
  requestCatchInterceptors?: (err: Error) => void
  responseInterceptors?: (response: AxiosResponse<any>) => AxiosResponse<any>
  responseCatchInterceptors?: (err: Error) => void
}

const RESULT_OK_CODE = 0
const { createMessage, createErrorModal } = useMessage()

export const axiosTransform: AxiosTransform = {
  requestBeforeHook(config, opts) {
    const {
      joinPrefix,
      formatDate,
      joinTime,
      urlPrefix,
      transformRequestTimeExcludeKeys = [],
      trimExcludeKeys = [],
    } = opts

    if (joinPrefix) {
      config.url = `${urlPrefix}${config.url}`
    }

    const params = config.params || {}
    const data = config.data || null

    if (formatDate && data) {
      formatDataDate(data, transformRequestTimeExcludeKeys, trimExcludeKeys)
    }

    if (joinTime && config.method?.toUpperCase() === HttpMethod.GET) {
      if (false === isString(params)) {
        config.params = Object.assign(params, joinTimeStamp(false))
      } else {
        config.params = undefined
        config.url = config.url + params + joinTimeStamp(true)
      }
    }

    if (config.method?.toUpperCase() === HttpMethod.POST) {
      config.data = data || {}
      if (config.data instanceof FormData) {
        config.data.append('requestId', uuid())
      } else {
        config.data.requestId = uuid()
      }
    }

    return config
  },
  transformResponseHook(response, opts) {
    const {
      isReturnNativeResponse,
      isTransformResponse,
      isTransformResponseTime2WarehouseTime = true,
    } = opts

    if (isReturnNativeResponse) {
      return response
    }

    if (isTransformResponse === false) {
      return response.data
    }

    const resData = response.data

    if (!resData) {
      throw new Error('接口数据返回错误')
    }

    const { code, message, data } = resData
    if (code !== undefined && (code === RESULT_OK_CODE || code === 200)) {
      isTransformResponseTime2WarehouseTime && transformResponseTime(data)
      return data
    }

    checkException(Number(code), message, opts.errorMessageMode)

    throw new Error(message)
  },
  requestInterceptors(config, opts) {
    const store = useUserStoreWithOut()
    const token = store.getToken
    if (token && opts.requestOptions?.withToken) {
      config.headers!.Authorization = opts.authenticationScheme
        ? `${opts.authenticationScheme} ${token}`
        : token
    }

    // 添加language
    // const { locale } = useLocale()
    config.headers!['Accept-Language'] = getLocaleInfoCache(false)?.locale || 'zh_CN'

    config.headers!['client-type'] = 'web'
    // config.headers!['Host'] = location.host

    const regionWarehouseCode = storageProxy.get('warehouse_code')
    config.headers!['Warehousecode'] = regionWarehouseCode

    return config
  },
  requestCatchInterceptors(error) {
    throw new Error('请求配置错误： ' + error)
  },
  responseInterceptors(response) {
    return response
  },
  responseCatchInterceptors(error: any) {
    const { response, code, message, config } = error || {}
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none'
    const msg: string = response?.data?.message ?? ''
    const err: string = error?.toString?.() ?? ''
    let errMessage = ''
    if (code === 'ECONNABORTED' && message.indexOf('timeout') !== -1) {
      errMessage = '网络超时'
    }
    if (err?.includes('Network Error')) {
      errMessage = '网络错误'
    }
    if (errMessage) {
      if (errorMessageMode === 'modal') {
        createErrorModal({ title: '网络错误', content: errMessage })
      } else if (errorMessageMode === 'message') {
        createMessage.error(errMessage)
      }
      return Promise.reject(error)
    }
    checkException(error?.response?.status, msg, errorMessageMode)
    return Promise.reject(error)
  },
}
